import React, { useEffect, useState, useCallback } from "react";
import { useDisclosure, useToast } from "@chakra-ui/react";
import FolderDrawer from "./folder-drawer";
import FileDrawer from "./file-drawer";
import CreateModal from "./create-modal";
import { EditModal } from "./EditModal";
import { ModalDelete } from "@root/components/modal-delete";
import useAdminUsers from "@root/hooks/useAdminUsers";
import { useTranslation } from "react-i18next";
import { FILE_MANAGEMENT, PAGINATION_DEFAULTS } from "@root/constants";
import { useProjectsContext } from "@root/context/projects-context";

export const DrawerFlow: React.FC<DrawerFlowProps> = ({ onClose }) => {
  const [selectedFolder, setSelectedFolder] = useState<FolderData | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [progress, setProgress] = useState(0);
  const [createType, setCreateType] = useState<
    FILE_MANAGEMENT.FOLDER | FILE_MANAGEMENT.FILE | "project"
  >(FILE_MANAGEMENT.FOLDER);
  const [editingEntity, setEditingEntity] = useState<{
    type: "project" | "folder";
    data: Project | FolderData;
  } | null>(null);
  const [deletingEntity, setDeletingEntity] = useState<{
    type: "project" | "folder" | "file";
    data: Project | FolderData | FileData;
  } | null>(null);
  const [projectPage, setProjectPage] = useState(1);
  const [projectCount] = useState(0); // Will be updated from projects.length if needed

  const folderDrawer = useDisclosure();
  const fileDrawer = useDisclosure();
  const modal = useDisclosure();
  const editModal = useDisclosure();
  const deleteModal = useDisclosure();
  const toast = useToast();
  const { t } = useTranslation("common");
  const {
    getFolders,
    folders,
    createFolder,
    foldersLoading,
    files,
    getFiles,
    uploadFile,
    uploadFileLoading,
    folderCount,
    folderPage,
    setFolderPage,
    fileCount,
    setFilePage,
    filePage,
    DeleteFile,
    fileDeleteLoading,
    DeleteFolder,
    filesLoading,
    createFolderLoading,
    folderDeleteLoading,
    retryFile,
  }: any = useAdminUsers();

  // UseProjectsContext for all project CRUD operations
  const {
    addProject,
    updateProject,
    deleteProject,
    addProjectLoading,
    getProjectsLoading,
    updateProjectLoading,
    deleteProjectLoading,
    updateFolder,
    updateFolderLoading,
    fetchProjects,
    projectsData,
  } = useProjectsContext();

  // Refetch projects after CRUD operations
  const refetchProjects = useCallback(async () => {
    await fetchProjects({
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      page: projectPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPage, fetchProjects]); // Depend on projectPage and fetchProjects

  const uploadFileInput = async (payload: any) => {
    if (!payload?.file || !selectedFolder) return;

    try {
      const formData = new FormData();
      formData.append("folderId", payload.folderId.toString());
      formData.append("file", payload.file);
      formData.append("name", payload.name);

      await uploadFile(formData);

      toast({ title: t("projects.fileUploadSuccess"), status: "success" });
      modal.onClose();
      setTimeout(() => setProgress(0), 1000);
    } catch (error) {
      toast({ title: t("projects.fileUploadFailed"), status: "error" });
    }
  };

  const handleFolderClick = async (folder: FolderData) => {
    setSelectedFolder(folder);
    setFilePage(1); // Reset to page 1 when selecting new folder
    fileDrawer.onOpen();
  };

  // Folder delete handler - shows modal first
  const handleDeleteFolderClick = (folder: FolderData) => {
    setDeletingEntity({ type: "folder", data: folder });
    deleteModal.onOpen();
  };

  const confirmDeleteFolder = async () => {
    if (
      !deletingEntity ||
      deletingEntity.type !== "folder" ||
      !selectedProject?.id
    ) {
      return;
    }

    try {
      const folderData = deletingEntity.data as FolderData;
      await DeleteFolder({ id: folderData.id });
      // Close modal immediately on API success
      deleteModal.onClose();
      setDeletingEntity(null);
      // refetch folders after modal closes
      await getFolders({
        projectId: selectedProject.id,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        page: folderPage,
      });
    } catch (error) {
      console.error("Failed to delete folder:", error);
      // Don't close modal on error
    }
  };

  // Project delete handler - shows modal first
  const handleDeleteProjectClick = (project: Project) => {
    setDeletingEntity({ type: "project", data: project });
    deleteModal.onOpen();
  };

  const confirmDeleteProject = async () => {
    if (!deletingEntity || deletingEntity.type !== "project") {
      return;
    }

    try {
      const projectData = deletingEntity.data as Project;
      await deleteProject(projectData.id);
      // Close modal immediately on API success
      deleteModal.onClose();
      setDeletingEntity(null);
      // Refetch projects after modal closes
      await refetchProjects();
      // If deleted project was selected, clear selection
      if (selectedProject?.id === projectData.id) {
        setSelectedProject(null);
      }
    } catch (error) {
      console.error("Failed to delete project:", error);
      // Don't close modal on error
    }
  };

  // File delete handler - shows modal first
  const handleDeleteFileClick = (file: FileData) => {
    setDeletingEntity({ type: "file", data: file });
    deleteModal.onOpen();
  };

  // File retry handler
  const handleRetryFileClick = async (file: FileData) => {
    if (!selectedProject?.id || !file?.id) {
      return;
    }

    try {
      const fileData = file as FileData & {
        fileName_key?: string;
        fileName?: string;
      };
      await retryFile({
        project_id: selectedProject.id,
        fileName_key: fileData?.fileName_key ?? "",
        fileName: fileData?.fileName ?? "",
        id: file.id,
      });
      // Refresh files after successful retry
      if (selectedFolder?.id) {
        getFiles({
          folderId: selectedFolder.id,
          limit: PAGINATION_DEFAULTS.PAGESIZE,
          page: filePage,
        });
      }
    } catch (error) {
      console.error("Failed to retry file:", error);
    }
  };

  const confirmDeleteFile = async () => {
    if (
      !deletingEntity ||
      deletingEntity.type !== "file" ||
      !selectedFolder?.id
    ) {
      return;
    }

    try {
      const fileData = deletingEntity.data as FileData;
      await DeleteFile({ id: fileData.id });
      // Close modal immediately on API success
      deleteModal.onClose();
      setDeletingEntity(null);
      // refetch files after modal closes
      await getFiles({
        folderId: selectedFolder.id,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        page: filePage,
      });
    } catch (error) {
      console.error("Failed to delete file:", error);
      // Don't close modal on error
    }
  };

  // Edit handlers
  const handleEditProject = (project: Project) => {
    setEditingEntity({ type: "project", data: project });
    editModal.onOpen();
  };

  const handleEditFolder = (folder: FolderData) => {
    setEditingEntity({ type: "folder", data: folder });
    editModal.onOpen();
  };

  const handleSaveEdit = async (name: string, description?: string) => {
    if (!editingEntity) return;

    try {
      if (editingEntity.type === "project") {
        const projectData = editingEntity.data as Project;
        await updateProject(projectData.id, {
          name,
        });
        // Close modal immediately on API success
        editModal.onClose();
        setEditingEntity(null);
        // Refetch projects after modal closes
        await refetchProjects();
        // Update selected project if it was the one edited
        if (selectedProject?.id === editingEntity.data.id) {
          const updatedProject: Project = {
            id: selectedProject.id,
            name,
          };
          setSelectedProject(updatedProject);
        }
      } else if (editingEntity.type === "folder" && selectedProject?.id) {
        const folderData = editingEntity.data as FolderData;
        await updateFolder(Number(folderData.id), {
          name,
          projectId: selectedProject.id,
        });
        // Close modal immediately on API success
        editModal.onClose();
        setEditingEntity(null);
        // Refetch folders after modal closes
        await getFolders({
          projectId: selectedProject.id,
          limit: PAGINATION_DEFAULTS.PAGESIZE,
          page: folderPage,
        });
      }
    } catch (error) {
      console.error("Failed to update:", error);
      // Don't close modal on error
    }
  };

  // Fetch files when folder or page changes
  useEffect(() => {
    if (selectedFolder?.id) {
      getFiles({
        folderId: selectedFolder.id,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        page: filePage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePage, selectedFolder?.id]); // Only depend on folder ID, not the whole object

  const handleCreateClick = async (
    type: FILE_MANAGEMENT.FOLDER | FILE_MANAGEMENT.FILE | "project",
    payload: any
  ) => {
    if (type === "project") {
      try {
        await addProject(payload);
        // Close modal immediately on API success
        modal.onClose();
        // Refetch projects after modal closes
        await refetchProjects();
      } catch (error) {
        console.error("Failed to create project:", error);
      }
    } else if (type === FILE_MANAGEMENT.FOLDER) {
      try {
        await createFolder(payload);
        // Close modal immediately on API success
        modal.onClose();
      } catch (error) {
        console.error("Failed to create folder:", error);
        // Don't close modal on error
      }
    } else {
      uploadFileInput(payload);
    }
  };

  // Fetch projects only when drawer opens and when page changes
  useEffect(() => {
    if (!folderDrawer.isOpen) return; // Only fetch when drawer is open

    const fetchProjectsData = async () => {
      try {
        await fetchProjects({
          limit: PAGINATION_DEFAULTS.PAGESIZE,
          page: projectPage,
        });
        // Note: fetchProjects from useProjectsContext sets getProjectsLoading state internally
      } catch (error) {
        console.error("Failed to fetch projects:", error);
      }
    };

    fetchProjectsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPage, folderDrawer.isOpen]); // Depend on page, drawer open state, and fetchProjects

  // Open drawer on mount - only once
  useEffect(() => {
    folderDrawer.onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  return (
    <>
      <FolderDrawer
        isOpen={folderDrawer.isOpen}
        onClose={() => {
          folderDrawer.onClose();
          onClose?.();
        }}
        folders={folders}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        getFolders={getFolders}
        projects={projectsData?.rows || []}
        onFolderClick={handleFolderClick}
        folderCount={folderCount}
        folderPage={folderPage}
        setFolderPage={setFolderPage}
        projectPage={projectPage}
        setProjectPage={setProjectPage}
        projectCount={projectCount}
        onDeleteFolder={handleDeleteFolderClick}
        onDeleteProject={handleDeleteProjectClick}
        onEditFolder={handleEditFolder}
        onEditProject={handleEditProject}
        foldersLoading={foldersLoading}
        projectsLoading={getProjectsLoading}
        onCreateClick={() => {
          if (selectedProject) {
            setCreateType(FILE_MANAGEMENT.FOLDER);
          } else {
            setCreateType("project");
          }
          modal.onOpen();
        }}
      />

      <FileDrawer
        isOpen={fileDrawer.isOpen}
        folderDrawerOnOpen={folderDrawer.onOpen}
        onClose={fileDrawer.onClose}
        selectedFolder={selectedFolder}
        files={files}
        fileCount={fileCount}
        filePage={filePage}
        setFilePage={setFilePage}
        deleteFileHandler={handleDeleteFileClick}
        retryFileHandler={handleRetryFileClick}
        onCreateClick={() => {
          setCreateType(FILE_MANAGEMENT.FILE);
          modal.onOpen();
        }}
        fileLoading={filesLoading}
        getFiles={getFiles}
      />

      <CreateModal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        folderLoading={createFolderLoading}
        fileUploadLoading={uploadFileLoading}
        projectLoading={addProjectLoading}
        getFiles={getFiles}
        getFolders={getFolders}
        folders={folders}
        progress={progress}
        setProgress={setProgress}
        createType={createType}
        handleCreateClick={handleCreateClick}
        selectedFolder={selectedFolder}
        selectedProject={selectedProject}
        setFilePage={setFilePage}
        setFolderPage={setFolderPage}
      />

      {/* Edit Modal */}
      {editingEntity && (
        <EditModal
          isOpen={editModal.isOpen}
          onClose={() => {
            editModal.onClose();
            setEditingEntity(null);
          }}
          onSave={handleSaveEdit}
          initialName={editingEntity.data.name}
          initialDescription={undefined}
          isLoading={
            editingEntity.type === "project"
              ? updateProjectLoading
              : updateFolderLoading
          }
          entityType={editingEntity.type}
        />
      )}

      {/* Delete Modal */}
      <ModalDelete
        isOpen={deleteModal.isOpen}
        onClose={() => {
          deleteModal.onClose();
          setDeletingEntity(null);
        }}
        onConfirm={() => {
          if (deletingEntity?.type === "project") {
            confirmDeleteProject();
          } else if (deletingEntity?.type === "folder") {
            confirmDeleteFolder();
          } else if (deletingEntity?.type === "file") {
            confirmDeleteFile();
          }
        }}
        title={t("chat.areYouSureDelete")}
        description={t("chat.cannotBeUndone")}
        deleteButtonTitle={t("chat.deleteBtn", { defaultValue: "Delete" })}
        cancelButtonTitle={t("chat.cancelBtn", { defaultValue: "Cancel" })}
        isLoading={
          deletingEntity?.type === "project"
            ? deleteProjectLoading
            : deletingEntity?.type === "folder"
              ? folderDeleteLoading
              : fileDeleteLoading
        }
      />
    </>
  );
};

export default DrawerFlow;
